import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { AbstractCmsBlockComponent } from '../../../cms/block/abstract-cms-block.component';

export interface ILoginFormContentText extends NICmsContentComponent.IData {
    // is not checked
    not_checked_header: string;
    not_checked_additional_text: string;
    // is checked and registered
    registered_header: string;
    registered_additional_text: string;
    // is checked and not registered
    not_registered_header: string;
    not_registered_additional_text: string;
}

@Component({
    selector: 'login-form-content-text',
    templateUrl: './login-form-content-text.html',
    standalone: true,
    imports: [NgClass, ICmsClientModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<LoginFormContentTextComponent>({
    id: 'login-form-content-text',
    block: {
        url: 'login-form-content-text'
    },
    onDemandOnly: true,
    name: 'Login Form Content Text',
    description: 'Text content used in login form',
    fields: [
        <NICmsContentField.IField>{
            key: 'not_checked',
            type: NICmsContentField.EFieldType.label,
            title: 'Content for not checked user'
        },
        <NICmsContentField.IField>{
            key: 'not_checked_header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header',
            default: 'Enter your email to log in or sign up',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'not_checked_additional_text',
            type: NICmsContentField.EFieldType.text,
            title: 'Additional text',
            default: 'We’ll check if you have an OB account',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'registered',
            type: NICmsContentField.EFieldType.label,
            title: 'Content for registered user'
        },
        <NICmsContentField.IField>{
            key: 'registered_header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header',
            default: 'Welcome back',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'registered_additional_text',
            type: NICmsContentField.EFieldType.text,
            title: 'Additional text',
            default: 'Sign in to your account to enjoy quicker checkout, save items to your wishlist, view your order history, and more.',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'not_registered',
            type: NICmsContentField.EFieldType.label,
            title: 'Content for not registered user'
        },
        <NICmsContentField.IField>{
            key: 'not_registered_header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header',
            default: 'You’re new here, get £5 off your next order',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'not_registered_additional_text',
            type: NICmsContentField.EFieldType.text,
            title: 'Additional text',
            default:
                'Create an account and receive a welcome treat when you join. Plus, enjoy early access to sales and new collections, quicker checkout, and more.',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'fill_data',
            type: NICmsContentField.EFieldType.label,
            title: 'Content for guest user'
        },
        <NICmsContentField.IField>{
            key: 'fill_data_header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header',
            default: 'You’re new here, welcome!',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'fill_data_additional_text',
            type: NICmsContentField.EFieldType.text,
            title: 'Additional text',
            default: 'Enter your details. You can create an account later.',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'fill_data_already',
            type: NICmsContentField.EFieldType.label,
            title: 'Content for guest user - already has account'
        },
        <NICmsContentField.IField>{
            key: 'fill_data_already_header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header',
            default: 'Welcome back!',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'fill_data_already_additional_text',
            type: NICmsContentField.EFieldType.text,
            title: 'Additional text',
            default: 'Enter your details.',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'logout_success',
            type: NICmsContentField.EFieldType.label,
            title: 'Content for logged out user'
        },
        <NICmsContentField.IField>{
            key: 'logout_success_header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header',
            default: 'You have signed out',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'logout_success_additional_text',
            type: NICmsContentField.EFieldType.text,
            title: 'Additional text'
        }
    ]
})
export class LoginFormContentTextComponent extends AbstractCmsBlockComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: ILoginFormContentText;

    @Input()
    checked = false;

    @Input()
    shouldRegister = false;

    @Input()
    isLoggedIn = false;

    @Input()
    shouldFillRegisterData = false;

    @Input()
    isGuestCheckout = false;

    @Input()
    logoutSuccess = false;

    @Input()
    checkoutVariant = false;

    get currentStep(): string {
        if (this.logoutSuccess) {
            return 'logout_success';
        } else if (!this.checked) {
            return 'not_checked';
        } else if (!this.shouldRegister) {
            if (!this.isLoggedIn && this.shouldFillRegisterData) {
                if (!this.isGuestCheckout) {
                    return 'fill_data';
                }

                return 'fill_data_already';
            }

            return 'registered';
        }

        return 'not_registered';
    }

    get header(): string {
        return `${this.currentStep}_header`;
    }

    get additional_text(): string {
        return `${this.currentStep}_additional_text`;
    }

    get marginTop(): string {
        return this.checkoutVariant ? 'm-t-1 m-t-3-s m-t-3-m' : this.checked ? 'm-t m-t-7-s' : '';
    }
}
